import React, { useState, useEffect } from 'react';
import { X, Store, Building2, MapPin, User, AlertTriangle } from 'lucide-react';
import type {
  PointOfSale,
  City,
  Chain,
  RepresentativeOption,
} from '../../../types/api';

interface StoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    cityId: string;
    chainId: string;
    representativeId?: string | null;
  }) => Promise<void>;
  store?: PointOfSale | null;
  cities: City[];
  chains: Chain[];
  representatives: RepresentativeOption[];
  isLoading?: boolean;
}

export const StoreModal: React.FC<StoreModalProps> = ({
  isOpen,
  onClose,
  onSave,
  store,
  cities,
  chains,
  representatives,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    cityId: '',
    chainId: '',
    representativeId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Ciudad y zona seleccionada
  const selectedCity = cities.find((c) => c.id === formData.cityId);
  const cityZoneId = selectedCity?.zone.id;
  const cityZoneName = selectedCity?.zone.name;

  // ✅ CAMBIO: Mostrar TODOS los representantes, pero con indicador visual si no coincide la zona
  const allRepresentatives = representatives.map((rep) => ({
    ...rep,
    matchesZone: rep.zone.id === cityZoneId,
  }));

  // Representante seleccionado
  const selectedRep = representatives.find(
    (r) => r.id === formData.representativeId
  );
  const showZoneMismatchWarning =
    selectedRep && cityZoneId && selectedRep.zone.id !== cityZoneId;

  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name,
        cityId: store.city.id,
        chainId: store.chain.id,
        representativeId: store.representative?.id ?? '',
      });
    } else {
      setFormData({
        name: '',
        cityId: '',
        chainId: '',
        representativeId: '',
      });
    }
    setErrors({});
  }, [store, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.cityId) {
      newErrors.cityId = 'La ciudad es requerida';
    }
    if (!formData.chainId) {
      newErrors.chainId = 'La cadena es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onSave({
        name: formData.name,
        cityId: formData.cityId,
        chainId: formData.chainId,
        representativeId: formData.representativeId || null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving store:', error);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-lg w-full h-full sm:h-auto sm:max-w-md max-h-screen sm:max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {store ? 'Editar Punto de Venta' : 'Crear Punto de Venta'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Debug Info - Remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div className="p-2 bg-blue-50 rounded text-xs">
              <strong>Debug:</strong> Cities: {cities.length}, Chains:{' '}
              {chains.length}, Reps: {representatives.length}
            </div>
          )}

          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Store className="inline h-4 w-4 mr-1" />
              Nombre del Punto de Venta *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                handleInputChange('name', e.target.value);
              }}
              disabled={isLoading}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-950 disabled:bg-gray-100 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ej: Tienda Plaza Norte"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Ciudad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="inline h-4 w-4 mr-1" />
              Ciudad * {cities.length > 0 && `(${cities.length} disponibles)`}
            </label>
            <select
              value={formData.cityId}
              onChange={(e) => {
                handleInputChange('cityId', e.target.value);
              }}
              disabled={isLoading}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-950 disabled:bg-gray-100 ${
                errors.cityId ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Selecciona una ciudad</option>
              {cities.length === 0 ? (
                <option disabled>No hay ciudades disponibles</option>
              ) : (
                cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name} ({city.zone.name})
                  </option>
                ))
              )}
            </select>
            {errors.cityId && (
              <p className="mt-1 text-sm text-red-600">{errors.cityId}</p>
            )}
            {cities.length === 0 && (
              <p className="mt-1 text-sm text-orange-600">
                ⚠️ No se cargaron las ciudades. Intenta actualizar la página.
              </p>
            )}
          </div>

          {/* Cadena */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Cadena * {chains.length > 0 && `(${chains.length} disponibles)`}
            </label>
            <select
              value={formData.chainId}
              onChange={(e) => {
                handleInputChange('chainId', e.target.value);
              }}
              disabled={isLoading}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-950 disabled:bg-gray-100 ${
                errors.chainId ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Selecciona una cadena</option>
              {chains.length === 0 ? (
                <option disabled>No hay cadenas disponibles</option>
              ) : (
                chains.map((chain) => (
                  <option key={chain.id} value={chain.id}>
                    {chain.name} ({chain.zone.name})
                  </option>
                ))
              )}
            </select>
            {errors.chainId && (
              <p className="mt-1 text-sm text-red-600">{errors.chainId}</p>
            )}
            {chains.length === 0 && (
              <p className="mt-1 text-sm text-orange-600">
                ⚠️ No se cargaron las cadenas. Intenta actualizar la página.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline h-4 w-4 mr-1" />
              Representante (Opcional){' '}
              {representatives.length > 0 &&
                `(${representatives.length} disponibles)`}
            </label>
            <select
              value={formData.representativeId}
              onChange={(e) => {
                handleInputChange('representativeId', e.target.value);
              }}
              disabled={isLoading}
              className="w-full px-3 py-2.5 sm:py-2 sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-950 disabled:bg-gray-100"
            >
              <option value="">Sin asignar</option>
              {representatives.length === 0 ? (
                <option disabled>No hay representantes disponibles</option>
              ) : (
                allRepresentatives.map((rep) => (
                  <option
                    key={rep.id}
                    value={rep.id}
                    className={rep.matchesZone ? '' : 'text-orange-600'}
                  >
                    {rep.user.name} - #{rep.user.employeeNumber} (
                    {rep.zone.name}){!rep.matchesZone && cityZoneName && ` ⚠️`}
                  </option>
                ))
              )}
            </select>

            {/* Info sobre filtrado por zona */}
            {formData.cityId && cityZoneName && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800">
                💡 Ciudad seleccionada está en zona{' '}
                <strong>{cityZoneName}</strong>. Los representantes de otras
                zonas se muestran con ⚠️
              </div>
            )}

            {/* Advertencia de zona diferente */}
            {showZoneMismatchWarning && selectedRep && (
              <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded flex items-start">
                <AlertTriangle className="h-5 w-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <strong>Advertencia:</strong> El representante{' '}
                  <strong>{selectedRep.user.name}</strong> (
                  {selectedRep.zone.name}) está en una zona diferente a la
                  ciudad <strong>{selectedCity?.name}</strong> ({cityZoneName}).
                </div>
              </div>
            )}

            {representatives.length === 0 && (
              <p className="mt-1 text-sm text-orange-600">
                ⚠️ No se cargaron representantes. Intenta actualizar la página.
              </p>
            )}

            {!formData.cityId && (
              <p className="mt-1 text-xs text-gray-500">
                Puedes seleccionar cualquier representante. Se recomienda que
                esté en la misma zona que la ciudad.
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-accent disabled:opacity-50 flex items-center"
              disabled={isLoading}
            >
              {isLoading && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isLoading ? 'Guardando...' : store ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
